import React, { useMemo } from "react";
import { Box, Card, CardContent, Typography, LinearProgress, Chip, Button, Stack, Alert, useTheme } from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CelebrationIcon from "@mui/icons-material/Celebration";
import WarningIcon from "@mui/icons-material/Warning";

interface TrialStatusCardProps {
    isInTrial: boolean;
    trialEnd: string | null;
    trialStarted: string | null;
    onUpgradeClick: () => void;
    serverTime?: Date; // Optional server time prop
}

const TrialStatusCard: React.FC<TrialStatusCardProps> = ({ isInTrial, trialEnd, trialStarted, onUpgradeClick, serverTime }) => {
    const theme = useTheme();

    // Calculate trial progress and remaining days
    const trialInfo = useMemo(() => {
        if (!isInTrial || !trialEnd || !trialStarted) {
            return null;
        }

        // Use server time if provided, otherwise fall back to client time
        const now = serverTime || new Date();
        const endDate = new Date(trialEnd);
        const startDate = new Date(trialStarted);

        const totalDuration = endDate.getTime() - startDate.getTime();
        const elapsed = now.getTime() - startDate.getTime();
        const remaining = endDate.getTime() - now.getTime();

        const daysRemaining = Math.ceil(remaining / (1000 * 60 * 60 * 24));
        const totalDays = Math.ceil(totalDuration / (1000 * 60 * 60 * 24));
        const progress = Math.max(0, Math.min(100, (elapsed / totalDuration) * 100));

        const endDateFormatted = endDate.toLocaleDateString("de-DE", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        });

        return {
            daysRemaining,
            totalDays,
            progress,
            endDateFormatted,
            isExpiringSoon: daysRemaining <= 2,
            isExpired: daysRemaining <= 0,
        };
    }, [isInTrial, trialEnd, trialStarted, serverTime]);

    // Don't render if not in trial
    if (!isInTrial || !trialInfo) {
        return null;
    }

    const getStatusColor = () => {
        if (trialInfo.isExpired) return "error";
        if (trialInfo.isExpiringSoon) return "warning";
        return "success";
    };

    const getStatusIcon = () => {
        if (trialInfo.isExpired) return <WarningIcon />;
        if (trialInfo.isExpiringSoon) return <AccessTimeIcon />;
        return <CelebrationIcon />;
    };

    const getStatusText = () => {
        if (trialInfo.isExpired) return "Testphase abgelaufen";
        if (trialInfo.isExpiringSoon) return `Noch ${trialInfo.daysRemaining} Tag${trialInfo.daysRemaining === 1 ? "" : "e"}`;
        return `${trialInfo.daysRemaining} Tage verbleibend`;
    };

    const getUrgencyMessage = () => {
        if (trialInfo.isExpired) {
            return "Deine Testphase ist abgelaufen. Wähle ein Abonnement, um ESGbot weiter zu nutzen.";
        }
        if (trialInfo.isExpiringSoon) {
            return `Deine Testphase endet am ${trialInfo.endDateFormatted}.`;
        }
        return `Entdecke alle Funktionen bis zum ${trialInfo.endDateFormatted}. Jederzeit kündbar.`;
    };

    return (
        <Card
            sx={{
                background: `linear-gradient(135deg, ${theme.palette.primary.main}15, ${theme.palette.secondary.main}10)`,
                border: `2px solid ${theme.palette[getStatusColor()].main}`,
                position: "relative",
                overflow: "visible",
            }}
        >
            {/* Trial Badge */}
            <Chip
                icon={getStatusIcon()}
                label="TESTPHASE"
                color={getStatusColor()}
                sx={{
                    position: "absolute",
                    top: 12,
                    right: 16,
                    fontWeight: "bold",
                    fontSize: "0.75rem",
                }}
            />

            <CardContent sx={{ pt: 3 }}>
                <Stack spacing={3}>
                    {/* Header */}
                    <Box>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                            🎉 Willkommen bei ESGbot!
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Du nutzt derzeit die kostenlose 7-Tage-Testversion
                        </Typography>
                    </Box>

                    {/* Progress Section */}
                    <Box>
                        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                            <Typography variant="body2" fontWeight="medium">
                                Fortschritt
                            </Typography>
                            <Typography variant="body2" fontWeight="bold" color={`${getStatusColor()}.main`}>
                                {getStatusText()}
                            </Typography>
                        </Box>

                        <LinearProgress
                            variant="determinate"
                            value={trialInfo.progress}
                            color={getStatusColor()}
                            sx={{
                                height: 8,
                                borderRadius: 4,
                                backgroundColor: `${theme.palette[getStatusColor()].main}20`,
                            }}
                        />

                        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                                Tag 1
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Tag {trialInfo.totalDays}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Status Message */}
                    <Alert
                        severity={getStatusColor()}
                        variant="outlined"
                        sx={{
                            backgroundColor: "transparent",
                            "& .MuiAlert-message": { fontSize: "0.875rem" },
                        }}
                    >
                        {getUrgencyMessage()}
                    </Alert>
                </Stack>
            </CardContent>
        </Card>
    );
};

export default TrialStatusCard;
